// Э-14. Приложение водителя (ФТ-9.1–9.3): мои рейсы, место в очереди, время погрузки, адрес с переходом в навигатор,
// телефон прораба, статусы в одно касание. Без связи отметки и координаты копятся на телефоне и уходят позже.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, uid } from "../../shared/api";
import { fmtNum, fmtPhone, fmtTime, telHref } from "../../shared/format";
import { Button, Loading, NetBar, Plate, useAsync, useToast } from "../../shared/ui";
import { api, queue } from "../ctx";

const ACTION: Record<string, string> = {
  queued: "Я на заводе",
  on_site: "Я на объекте",
  unloading: "Начал разгрузку",
  returning: "Разгрузился, еду на завод",
  completed: "Вернулся на завод",
};
const HEAD: Record<string, string> = {
  assigned: "Следующий рейс — езжайте на завод",
  queued: "Вы на заводе — ждите погрузку",
  loading: "Погрузка",
  departed: "В пути на объект",
  on_site: "На объекте",
  unloading: "Разгрузка",
  returning: "Возвращайтесь на завод",
};

type Trip = {
  id: string; status: string; order_number: number; seq: number; trips_total: number; grade: string; volume_m3: number;
  plate: string | null; customer: string; comment: string | null; pour_method: string;
  plant: { name: string; unit: string; address: string | null; lat: number | null; lng: number | null };
  site: { name: string; address: string | null; lat: number | null; lng: number | null };
  foreman: { name: string; phone: string | null } | null;
  load_start: string; planned_arrival: string; eta: string | null; queue_position: number | null;
  queue_ahead: { plate: string | null; status: string; load_start: string } | null;
  waybill_number: number | null; actions: string[];
};
type Home = {
  driver: { full_name: string; phone: string; plate: string | null };
  current: Trip | null; next: Trip[]; done_today: number; dispatch_phone: string; gps_interval_seconds: number;
  auto_status: boolean; vehicle_place: string | null;
};

/** Обычная ссылка: на телефоне откроет Яндекс Карты или Навигатор с маршрутом. */
export function navLink(p: { lat: number | null; lng: number | null; address: string | null }): string | null {
  if (p.lat != null && p.lng != null) return `https://yandex.ru/maps/?rtext=~${p.lat},${p.lng}&rtt=auto`;
  if (p.address) return `https://yandex.ru/maps/?text=${encodeURIComponent(p.address)}`;
  return null;
}

export function DriverPage({ online, queued, onLogout }: { online: boolean; queued: number; onLogout: () => void }) {
  const toast = useToast();
  const home = useAsync<Home>(() => api.get("/driver/home"), []);
  const reload = home.reload;
  // свои рейсы обновляем сами: диспетчер мог переназначить машину или перенести заявку
  useEffect(() => {
    const t = setInterval(() => { if (navigator.onLine) reload(); }, 20_000);
    return () => clearInterval(t);
  }, [reload]);
  const gps = useGps(home.data?.gps_interval_seconds ?? 30, !!home.data?.current, reload);

  if (!home.data) {
    return (
      <div className="driver">
        <NetBar online={online} queued={queued} />
        <Loading error={home.error} onRetry={reload} />
        {home.error && <div className="content"><Button onClick={onLogout}>Выйти</Button></div>}
      </div>
    );
  }
  const d = home.data;
  const cur = d.current;
  const act = async (t: Trip, status: string) => {
    try {
      const r = await queue.send("POST", `/driver/trips/${t.id}/status`, { status, at: new Date().toISOString() }, `${ACTION[status]}: заявка №${t.order_number}`);
      toast.ok(r === null ? "Нет связи: отметка сохранена на телефоне и уйдёт сама" : "Отмечено");
      reload();
    } catch (e) { toast.error(e); reload(); }
  };

  return (
    <div className="driver">
      <NetBar online={online} queued={queued} />
      <header className="driver-head">
        <div>
          <b>{d.driver.full_name}</b>
          <div className="small muted">{d.driver.plate ? <Plate value={d.driver.plate} small /> : "машина не закреплена"}</div>
        </div>
        <div className="grow" />
        <span className="row tight small"><span className={`dot${online ? "" : " off"}`} />{online ? "Онлайн" : "Нет связи"}</span>
        <Button size="small" variant="ghost" onClick={onLogout}>Выйти</Button>
      </header>

      <GpsBanner state={gps} auto={d.auto_status} />

      <main className="driver-body stack">
        {!cur ? (
          <section className="driver-card">
            <h2>Рейсов пока нет</h2>
            <p className="muted">Как только диспетчер назначит вас на рейс, он появится здесь. Страница обновляется сама.</p>
            {d.driver.plate && (d.vehicle_place === "at_plant"
              ? <p className="small">Диспетчер видит: вы на заводе.</p>
              : <Button size="big" variant="primary" onClick={async () => {
                  try {
                    const r = await queue.send("POST", "/driver/at-plant", {}, "Я на заводе");
                    toast.ok(r === null ? "Нет связи: отметка уйдёт сама" : "Отмечено: диспетчер видит, что вы на заводе");
                    reload();
                  } catch (e) { toast.error(e); }
                }}>Я на заводе</Button>)}
            {d.done_today > 0 && <p>Сделано рейсов сегодня: <b>{d.done_today}</b></p>}
          </section>
        ) : (
          <TripCard t={cur} onAct={act} />
        )}

        {d.next.length > 0 && (
          <section className="stack tight">
            <h3>Дальше</h3>
            {d.next.map((t) => (
              <div key={t.id} className="driver-next">
                <div className="num strong">{fmtTime(t.load_start)}</div>
                <div className="grow">
                  <div>{t.site.name}</div>
                  <div className="small muted">№{t.order_number}, рейс {t.seq} · {t.grade}, {fmtNum(t.volume_m3)} м³ · {t.plant.name}</div>
                </div>
              </div>
            ))}
          </section>
        )}

        {d.dispatch_phone && <a className="btn big" href={telHref(d.dispatch_phone)}>Позвонить в диспетчерскую</a>}
        {cur && d.done_today > 0 && <p className="small muted">Сделано рейсов сегодня: {d.done_today}</p>}
      </main>
    </div>
  );
}

function TripCard({ t, onAct }: { t: Trip; onAct: (t: Trip, status: string) => void }) {
  const toSite = ["departed", "on_site", "unloading"].includes(t.status);
  const toPlant = ["assigned", "returning"].includes(t.status);
  const site = navLink(t.site);
  const plant = navLink(t.plant);
  return (
    <section className="driver-card stack">
      <div className="muted strong">{HEAD[t.status] || t.status}</div>
      <div style={{ fontSize: 24, fontWeight: 700 }}>{t.grade}, <span className="num">{fmtNum(t.volume_m3)} м³</span></div>
      <div>Заявка №{t.order_number}, рейс {t.seq} из {t.trips_total} · {t.customer}</div>

      {["assigned", "queued"].includes(t.status) && (
        <div className="driver-fact">
          <div>Погрузка <b className="num">~{fmtTime(t.load_start)}</b> · {t.plant.name}, {t.plant.unit}</div>
          {t.queue_position != null && <div>Место в очереди: <b className="num">{t.queue_position}</b>{t.queue_position === 1 ? " — вы следующий" : ""}</div>}
          {t.queue_ahead && (
            <div>Вы за машиной <b className="num">{t.queue_ahead.plate || "без номера"}</b>
              <span className="muted"> — {t.queue_ahead.status}, погрузка ~{fmtTime(t.queue_ahead.load_start)}</span></div>
          )}
        </div>
      )}
      {t.status === "loading" && <div className="driver-fact">Идёт погрузка на {t.plant.unit}. Оператор отметит выезд, накладная — у оператора.</div>}

      {toPlant && (
        <div className="stack tight">
          <div className="small muted">Завод</div>
          <div>{t.plant.name}{t.plant.address ? `, ${t.plant.address}` : ""}</div>
          {plant && <a className="btn" href={plant} target="_blank" rel="noreferrer">Маршрут на завод</a>}
        </div>
      )}

      <div className="stack tight">
        <div className="small muted">Объект</div>
        <div><b>{t.site.name}</b>{t.site.address ? `, ${t.site.address}` : ""}</div>
        {t.eta && toSite && <div>Прибытие <b className="num">~{fmtTime(t.eta)}</b></div>}
        {t.comment && <div className="alert info"><div className="t">Как проехать</div><div className="d" style={{ whiteSpace: "pre-wrap" }}>{t.comment}</div></div>}
        <div className="cols-2">
          {site && <a className={`btn${toSite ? " primary" : ""}`} href={site} target="_blank" rel="noreferrer">Открыть в навигаторе</a>}
          {t.foreman?.phone && <a className="btn" href={telHref(t.foreman.phone)}>Прораб: {t.foreman.name}</a>}
        </div>
        {t.foreman?.phone && <div className="small muted">{fmtPhone(t.foreman.phone)}</div>}
      </div>

      {t.waybill_number && <div className="small muted">Накладная №{t.waybill_number}</div>}

      <div className="stack tight">
        {t.actions.map((a) => (
          <Button key={a} size="big" variant="primary" onClick={() => onAct(t, a)}>{ACTION[a] || a}</Button>
        ))}

      </div>
    </section>
  );
}

// ---------------------------------------------------------------- координаты (ФТ-9.2)

type GpsState = { status: "off" | "wait" | "on" | "denied" | "unsupported"; last: Date | null; pending: number };
type Pt = { at: string; lat: number; lng: number; accuracy_m: number | null; speed_kmh: number | null };
const BUF = "rbs.beton.gps";

function readBuf(): Pt[] {
  try { return JSON.parse(localStorage.getItem(BUF) || "[]"); } catch { return []; }
}
function writeBuf(p: Pt[]): void {
  try { localStorage.setItem(BUF, JSON.stringify(p.slice(-2000))); } catch { /* только в памяти */ }
}

/** Пока есть рейс — координаты раз в N секунд; без связи копятся на телефоне и уходят пачкой. */
function useGps(intervalSec: number, active: boolean, onStatus: () => void): GpsState {
  const [st, setSt] = useState<GpsState>({ status: "off", last: null, pending: readBuf().length });
  const lastSaved = useRef(0);
  const sending = useRef(false);
  const cb = useRef(onStatus);
  cb.current = onStatus;

  const flush = useCallback(async () => {
    if (sending.current || !navigator.onLine) return;
    const pts = readBuf();
    if (!pts.length) return;
    sending.current = true;
    try {
      const batch = pts.slice(0, 300);
      const r = await api.post<{ statuses: unknown[] }>("/driver/positions", { points: batch }, { "Idempotency-Key": uid() });
      writeBuf(readBuf().slice(batch.length));
      setSt((s) => ({ ...s, pending: readBuf().length }));
      if (r.statuses.length) cb.current();
    } catch (e) {
      // сервер отклонил пачку (например, неверные точки) — не держим её вечно
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 401) writeBuf(readBuf().slice(300));
    } finally {
      sending.current = false;
    }
  }, []);

  useEffect(() => {
    if (!active) { setSt((s) => ({ ...s, status: "off" })); return; }
    if (!("geolocation" in navigator)) { setSt((s) => ({ ...s, status: "unsupported" })); return; }
    setSt((s) => ({ ...s, status: s.status === "on" ? "on" : "wait" }));
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        setSt((s) => ({ ...s, status: "on", last: new Date() }));
        if (now - lastSaved.current < intervalSec * 1000) return;
        lastSaved.current = now;
        const p: Pt = {
          at: new Date(pos.timestamp || now).toISOString(),
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy ?? null,
          speed_kmh: pos.coords.speed != null ? Math.round(pos.coords.speed * 3.6) : null,
        };
        writeBuf([...readBuf(), p]);
        setSt((s) => ({ ...s, pending: readBuf().length }));
        flush();
      },
      (err) => setSt((s) => ({ ...s, status: err.code === err.PERMISSION_DENIED ? "denied" : "wait" })),
      { enableHighAccuracy: true, maximumAge: 10_000, timeout: 60_000 },
    );
    const t = setInterval(flush, intervalSec * 1000);
    window.addEventListener("online", flush);
    return () => { navigator.geolocation.clearWatch(id); clearInterval(t); window.removeEventListener("online", flush); };
  }, [active, intervalSec, flush]);
  return st;
}

function GpsBanner({ state, auto }: { state: GpsState; auto: boolean }) {
  if (state.status === "off") return null;
  const text = {
    wait: "Определяю местоположение…",
    on: `Местоположение передаётся${state.pending > 1 ? ` (ждут отправки: ${state.pending})` : ""}${auto ? " — статусы «на заводе», «на объекте», «вернулся» ставятся сами" : ". Статусы отмечайте кнопками"}`,
    denied: auto ? "Нет доступа к местоположению. Разрешите его в настройках браузера — тогда статусы будут ставиться сами. Пока отмечайте кнопками."
      : "Нет доступа к местоположению — отмечайте статусы кнопками.",
    unsupported: "Телефон не передаёт местоположение — отмечайте статусы кнопками.",
  }[state.status];
  return <div className={`driver-gps ${state.status}`}>{text}</div>;
}
